import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../utils/axios';
import TsdPageBar from '../components/tsd/TsdPageBar';
import TsdScanCapture from '../components/tsd/TsdScanCapture';
import FulfillmentStatusChip from '../components/fulfillment/FulfillmentStatusChip';
import FulfillmentProductThumb from '../components/fulfillment/FulfillmentProductThumb';
import LabelingStepCard from '../components/fulfillment/LabelingStepCard';
import ServiceLabelActions from '../components/fulfillment/ServiceLabelActions';
import { useNotify } from '../components/NotifyProvider';
import usePrinter from '../hooks/usePrinter';
import { PRINT_TIMEOUT_MESSAGE, TSD_PRINT_TIMEOUT_MS, createPrintActionKeys } from '../utils/printClient';
import { getWbImageUrl, toThumbnail } from '../utils/productImage';
import {
  LABELING_MODES,
  LABELING_STEPS,
  NO_CURRENT_TARGET_MESSAGE,
  NO_PRINTER_MESSAGE,
  SCAN_UNRECOGNIZED_MESSAGE,
  STEP_LOCKED_MESSAGES,
  allPickedNotice,
  fulfillmentApiBase,
  fulfillmentTsdPath,
  isCompositionAlertError,
  isDuringPickingMode,
  isServiceConfirmCode,
  labelingErrorMessage,
  labelingModeInfo,
  labelingStepHint,
  nextLabelingState,
  normalizeFulfillmentMp,
  normalizeLabelingOrder,
  pickingStockText,
  normalizePickingJobItem,
  pickingConfirmKey,
  pickingItemStageInfo,
  pickingLabelingCounts,
  pickingLabelingOrderPath,
  pickingLabelingPath,
  pickingTransferableCount,
  resumePickingVerify,
  scanErrorFeedback,
  scanStepBody,
  serviceScanNeedsPrinter,
  transferChipLabel,
  transferMoveInfo,
  withAccountBody,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';
import {
  addBatchScan,
  batchCounter,
  batchFromResolve,
  batchHasBarcode,
  batchScanAction,
  isActivePickingStatus,
  pickingProgress,
  sortPickingItemsByArticle,
  takeScan,
} from './pickingUi.mjs';
import { isB2bTask, pickingJobStatusInfo } from './fulfillmentUi.mjs';
import B2bChip from '../components/fulfillment/B2bChip';
import ConfirmDialog from '../components/fulfillment/ConfirmDialog';

const OTHER_PRODUCT_MESSAGE = STEP_LOCKED_MESSAGES.confirm;
const ALL_SCANNED_MESSAGE = 'Все единицы уже отсканированы';

function isUnrecognizedResolve(requestError) {
  const data = requestError?.response?.data || {};
  return data.code === 'barcode_unrecognized' || Number(requestError?.response?.status) === 404;
}

function labelDialogTitle(step, current) {
  if (step === LABELING_STEPS.VERIFY) return 'Отсканируйте этикетку';
  return current?.is_product ? 'Подтвердить и напечатать?' : 'Напечатать этикетку?';
}

function errorText(error) {
  if (!error?.response && error?.code === 'ECONNABORTED') return PRINT_TIMEOUT_MESSAGE;
  const data = error?.response?.data || {};
  if (data.code === 'product_all_picked') return allPickedNotice(data);
  if (data.code === 'item_transferred') {
    return data.message || 'Позиция уже передана на проклейку';
  }
  if (data.code === 'nothing_to_transfer') {
    return data.message || 'Нет собранных позиций для передачи';
  }
  if (data.code === 'transfer_move_error') {
    return data.message || 'Сначала повторите перемещение МойСклад для партии с ошибкой';
  }
  if (data.code === 'ms_out_of_stock') {
    return data.message || 'Недостаточно остатка МойСклад — подбор заблокирован';
  }
  if (data.code === 'ms_stock_unavailable') {
    return data.message || 'Остаток МойСклад сейчас недоступен. Проверьте настройки складов или креденшелы.';
  }
  const status = Number(error?.response?.status) || 0;
  const text = data.message || error?.message || 'Не удалось выполнить операцию';
  // 5xx: код HTTP в тексте — чтобы оператор мог передать его в поддержку, а не «что-то не так».
  return status >= 500 && !/HTTP \d{3}/.test(text) ? `${text} (HTTP ${status})` : text;
}

function settingsHint(message = '') {
  return /склад-источник|настройках кабинета|склады МойСклад/i.test(String(message || ''));
}

/** Нет связи МС↔МП: ведём в Настройки → Товары МС, после правки — «Обновить» в задании. */
function linkHint(message = '') {
  return /Товары МС/i.test(String(message || ''));
}

function warningSeverity(warning) {
  if (warning?.code === 'ms_product_unresolved' && /заблокирован/i.test(String(warning.message || ''))) return 'error';
  return 'warning';
}

const stockLabel = pickingStockText;
const STOCK_PENDING_REFRESH_MS = 3000;

/** Картинка: cards.image_url (product) → WB CDN; для Ozon WB CDN не используем. */
function ProductThumb({ target, marketplace }) {
  const preferred = target?.product?.image_url ? toThumbnail(target.product.image_url) : null;
  const nmId = target?.product?.nm_id ?? target?.task?.nm_id;
  const fallback = (marketplace === 'ozon' || marketplace === 'yandex') ? null : getWbImageUrl(nmId);
  const [src, setSrc] = useState(preferred || fallback);

  useEffect(() => {
    setSrc(preferred || fallback);
  }, [preferred, fallback]);

  if (!src) return null;
  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setSrc((current) => (fallback && current !== fallback ? fallback : null))}
      sx={{ width: 140, height: 180, objectFit: 'cover', borderRadius: 2, bgcolor: 'action.hover' }}
    />
  );
}

function canPickTarget(target) {
  if (!target?.stock) return true;
  // Явный запрет бэкенда (нет связи с МС при перемещении) — сильнее «остаток неизвестен».
  if (target.stock.can_pick === false) return false;
  // Неизвестный или устаревший остаток не блокирует: строгая проверка — на сервере при подтверждении.
  if (target.stock.known === false || target.stock.stale) return true;
  return Number(target.stock.available) > 0;
}

function stockBlocked(stock) {
  if (!stock) return false;
  if (stock.can_pick === false) return true;
  return Boolean(stock.known) && !stock.stale && Number(stock.available) <= 0;
}

function pickingItemLines(item) {
  const rows = item?.lines || item?.products;
  return Array.isArray(rows) && rows.length ? rows : null;
}

function compositionLines(item, marketplace) {
  if (marketplace === 'yandex') return pickingItemLines(item);
  const products = item?.task?.products_json || item?.posting?.products_json;
  if (!Array.isArray(products) || products.length < 2) return null;
  return products.map((product, index) => ({
    key: product.offer_id || product.sku || index,
    offer_id: product.offer_id || null,
    sku: product.sku || null,
    article: product.offer_id || null,
    name: product.name || null,
    barcode: product.barcode || null,
    quantity: product.quantity || 1,
    qty_need: product.quantity || 1,
    qty_picked: 0,
    status: item.status === 'picked' ? 'picked' : 'pending',
  }));
}

function lineCode(line) {
  return line?.offer_id || line?.article || line?.sku || 'Товар';
}

function lineName(line) {
  const name = String(line?.name || '').trim();
  const code = lineCode(line);
  if (!name || name === code) return '';
  return name;
}

export default function FulfillmentPickingJob() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTimer = useRef(null);

  const marketplace = normalizeFulfillmentMp(
    searchParams.get('mp') || searchParams.get('marketplace') || 'wb',
  ) || 'wb';
  const accountId = searchParams.get('account_id') || searchParams.get('accountId') || '';
  const apiBase = fulfillmentApiBase(marketplace);
  const isLocalWarehouse = marketplace === 'ozon' || marketplace === 'yandex';
  const listPath = fulfillmentTsdPath('/fulfillment/picking', { mp: marketplace, accountId });

  const [detail, setDetail] = useState(null);
  // Открытый диалог пачечного скана: товар + счётчик «собрано ранее / отсканировано сейчас».
  const [batch, setBatch] = useState(null);
  const batchRef = useRef(null);
  batchRef.current = batch;
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [transferConfirmOpen, setTransferConfirmOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const lastScanRef = useRef(null);

  // Режим during_picking: шаги idle → confirm (карточка товара) → verify (скан этикетки).
  const duringPicking = isDuringPickingMode(detail);
  const printer = usePrinter({ enabled: duringPicking });
  const printerUid = printer.selectedPrinter?.printer_uid || '';
  const {
    error: notifyError,
    info: notifyInfo,
    warning: notifyWarning,
    success: notifySuccess,
  } = useNotify();
  const [labelCurrent, setLabelCurrent] = useState(null);
  const [labelStep, setLabelStep] = useState(LABELING_STEPS.IDLE);
  const [printError, setPrintError] = useState(null);
  const [labelBusy, setLabelBusy] = useState(false);
  const labelBusyRef = useRef(false);
  const labelScanRef = useRef(null);
  const printKeysRef = useRef(null);
  if (!printKeysRef.current) printKeysRef.current = createPrintActionKeys();
  const printKeys = printKeysRef.current;
  const labelStateRef = useRef({ current: null, step: LABELING_STEPS.IDLE });
  labelStateRef.current = { current: labelCurrent, step: labelStep };

  const goToListSoon = useCallback((message) => {
    setBlockedReason(message);
    setDetail(null);
    if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
    redirectTimer.current = window.setTimeout(() => {
      navigate(listPath, { replace: true });
    }, 2500);
  }, [navigate, listPath]);

  useEffect(() => () => {
    if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
  }, []);

  const loadDetail = useCallback(async () => {
    if (!jobId) {
      goToListSoon('Не указан номер задания');
      setLoading(false);
      return;
    }
    if (isLocalWarehouse && !accountId) {
      setError('В ссылке нет account_id — откройте задание из списка подбора');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/picking-jobs/${jobId}`, {
        params: withAccountParams(marketplace, accountId),
      });
      if (!isActivePickingStatus(data?.status)) {
        const status = pickingJobStatusInfo(data?.status);
        goToListSoon(
          `Задание ${data?.code || ''} уже ${status?.label?.toLowerCase() || 'закрыто'}. Возврат к списку…`,
        );
        return;
      }
      setBlockedReason('');
      setDetail(data);
      // Напечатанную, но не проверенную этикетку нужно отсканировать до следующего товара.
      if (isDuringPickingMode(data) && labelStateRef.current.step === LABELING_STEPS.IDLE) {
        const resume = resumePickingVerify(
          (data.items || []).map((item) => normalizePickingJobItem(item, marketplace)),
          marketplace,
        );
        if (resume) {
          setLabelCurrent(resume);
          setLabelStep(LABELING_STEPS.VERIFY);
          setPrintError(null);
        }
      }
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status === 404 || status === 403) {
        goToListSoon('Задание не найдено или назначено другому сборщику. Возврат к списку…');
      } else {
        setError(errorText(requestError));
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, goToListSoon, apiBase, marketplace, accountId, isLocalWarehouse]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const resetLabelStep = useCallback(() => {
    setLabelCurrent(null);
    setLabelStep(LABELING_STEPS.IDLE);
    setPrintError(null);
  }, []);

  useEffect(() => {
    resetLabelStep();
    labelScanRef.current = null;
    printKeys.clear();
  }, [jobId, resetLabelStep, printKeys]);

  /** Фоновое обновление без сброса баннеров (после ошибок шага печати). */
  const refreshDetailQuiet = useCallback(async () => {
    if (!jobId) return;
    try {
      const { data } = await axios.get(`${apiBase}/picking-jobs/${jobId}`, {
        params: withAccountParams(marketplace, accountId),
        silent: true,
      });
      if (data?.id && Array.isArray(data.items)) setDetail(data);
    } catch {
      /* ошибку покажет следующее действие */
    }
  }, [jobId, apiBase, marketplace, accountId]);

  // Ответ скана с кэшем МС мог прийти без остатка — один тихий GET, когда фон его подгрузит.
  const stockPending = Boolean(detail?.stock_pending);
  useEffect(() => {
    if (!stockPending) return undefined;
    const timer = setTimeout(() => { void refreshDetailQuiet(); }, STOCK_PENDING_REFRESH_MS);
    return () => clearTimeout(timer);
  }, [stockPending, refreshDetailQuiet]);

  // Одинаковые товары подряд: сортировка по артикулу, затем по номеру заказа.
  const items = useMemo(
    () => sortPickingItemsByArticle(
      (detail?.items || []).map((item) => normalizePickingJobItem(item, marketplace)),
    ),
    [detail, marketplace],
  );

  const resolveOnServer = (code) => axios.post(
    `${apiBase}/picking-jobs/${detail.id}/scan/resolve`,
    withAccountBody(marketplace, accountId || detail.account_id, { barcode: code }),
  );

  /** Ещё одна единица текущего товара в открытом диалоге (локально, без записи на сервер). */
  const countScan = (code) => {
    const current = batchRef.current;
    const next = addBatchScan(current);
    if (!next.accepted) {
      setError(ALL_SCANNED_MESSAGE);
      return;
    }
    const barcodes = batchHasBarcode(current, code) ? next.batch.barcodes : [...next.batch.barcodes, code];
    setError('');
    setBatch({ ...next.batch, barcodes });
  };

  /** Скан при открытом диалоге: свой ШК → +1; незнакомый ШК → спросить сервер, тот ли товар. */
  const scanInBatch = async (code) => {
    const current = batchRef.current;
    if (!current) return;
    if (batchHasBarcode(current, code)) {
      countScan(code);
      return;
    }
    if (resolving) return;
    setResolving(true);
    const otherProduct = () => {
      setError(OTHER_PRODUCT_MESSAGE);
      notifyError(OTHER_PRODUCT_MESSAGE);
    };
    try {
      const { data } = await resolveOnServer(code);
      if (data?.product_key && data.product_key === current.product_key) {
        countScan(code);
      } else {
        otherProduct();
      }
    } catch (requestError) {
      const data = requestError?.response?.data || {};
      if (data.code === 'product_all_picked' && data.details?.product_key === current.product_key) {
        // Сервер считает товар собранным — локально единиц тоже не осталось.
        countScan(code);
      } else if (isUnrecognizedResolve(requestError)) {
        notifyWarning(SCAN_UNRECOGNIZED_MESSAGE);
      } else if (data.code === 'product_all_picked') {
        otherProduct();
      } else {
        setError(errorText(requestError));
      }
    } finally {
      setResolving(false);
    }
  };

  const resolve = async (raw) => {
    const code = String(raw || '').trim();
    if (!detail?.id || !code) return;
    // Антидребезг: тот же код в окне SCAN_REPEAT_MS — один физический скан, не вторая единица.
    if (!takeScan(lastScanRef, code, { busy: resolving })) return;
    const action = batchScanAction(code, Boolean(batchRef.current));
    if (action === 'confirm') {
      await confirm();
      return;
    }
    if (action === 'no_current') {
      notifyInfo(NO_CURRENT_TARGET_MESSAGE);
      return;
    }
    if (batchRef.current) {
      await scanInBatch(code);
      return;
    }
    setResolving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await resolveOnServer(code);
      setBatch(batchFromResolve(data, code));
    } catch (requestError) {
      const data = requestError?.response?.data || {};
      if (data.code === 'product_all_picked') notifyInfo(allPickedNotice(data));
      else if (isUnrecognizedResolve(requestError)) notifyWarning(SCAN_UNRECOGNIZED_MESSAGE);
      else setError(errorText(requestError));
    } finally {
      setResolving(false);
    }
  };

  /** «Отмена»: отсканированное в этом диалоге сбрасывается, на сервер ничего не пишется. */
  const closeConfirmation = () => {
    setBatch(null);
  };

  const confirm = async () => {
    const current = batchRef.current;
    if (!current || !detail) return;
    if (!canPickTarget(current)) {
      setError('Доступный остаток МойСклад равен нулю — подтвердить подбор нельзя');
      return;
    }
    const counter = batchCounter(current);
    const qty = counter.scanned_now;
    if (qty < 1) {
      closeConfirmation();
      return;
    }
    setResolving(true);
    try {
      const body = current.product_key
        ? { product_key: current.product_key, qty, scanned_barcode: current.scanned_barcode }
        : { item_id: current.confirmation_key, barcode: current.scanned_barcode };
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/picks/confirm`,
        withAccountBody(marketplace, accountId || detail.account_id, body),
      );
      const confirmed = Number.isFinite(Number(data?.confirmed_qty)) ? Number(data.confirmed_qty) : qty;
      const article = current.product?.article || '';
      if (data?.job_status !== 'completed') {
        setNotice(confirmed > 0
          ? `Собрано ${confirmed} шт${article ? ` · ${article}` : ''}`
          : 'Ничего не отмечено');
      }
      if (data?.warning?.message) {
        setError(data.warning.message);
      } else if (data?.stock_warning?.message) {
        setError(data.stock_warning.message);
      }
      closeConfirmation();
      if (data?.job_status === 'completed') {
        goToListSoon(`Задание ${detail.code || ''} закрыто. Возврат к списку…`);
      } else if (data?.job?.id && Array.isArray(data.job.items)) {
        setDetail(data.job);
      } else {
        await loadDetail();
      }
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setResolving(false);
    }
  };

  const transferTarget = duringPicking ? 'на склад отгрузки' : 'на проклейку';

  const labelBody = (extra = {}) => withAccountBody(marketplace, accountId || detail?.account_id, extra);

  const handleLabelError = (requestError) => {
    const data = requestError?.response?.data || {};
    const msg = labelingErrorMessage(data) || errorText(requestError);
    if (data.code === 'job_closed') {
      goToListSoon(`${msg}. Возврат к списку…`);
      return;
    }
    const stateCurrent = labelStateRef.current.current;
    if (isCompositionAlertError(data) && stateCurrent?.is_product
      && data.details?.product_key && data.details.product_key === stateCurrent.product_key) {
      // Ozon: единица ещё не подобрана — подбор и печать только кнопкой по товару, а не по отправлению.
      setLabelCurrent({ ...stateCurrent, composition_alert: true });
      setLabelStep(LABELING_STEPS.CONFIRM);
      setPrintError(null);
    } else if (isCompositionAlertError(data)) {
      // Ozon: состав изменился — служебный ШК отклонён, печать только кнопкой.
      const base = labelCurrent && !labelCurrent.is_product ? labelCurrent : null;
      const fromDetails = normalizeLabelingOrder(data.details?.order, marketplace)
        || (data.details?.order_key != null
          ? normalizeLabelingOrder({
            order_key: data.details.order_key,
            article: labelCurrent?.article,
            name: labelCurrent?.name,
            image_url: labelCurrent?.image_url,
            barcode: labelCurrent?.barcode,
          }, marketplace)
          : null);
      const order = fromDetails || base;
      if (order) {
        setLabelCurrent({ ...order, composition_alert: true });
        setLabelStep(LABELING_STEPS.CONFIRM);
      }
      setPrintError(null);
    } else if (requestError?.response) {
      // step_locked / «уже готово» / неизвестный ШК — только тост, шаг и окно не трогаем.
      const feedback = scanErrorFeedback(data);
      if (feedback.severity === 'info') {
        notifyInfo(feedback.message);
        return;
      }
      if (feedback.severity === 'warning') {
        notifyWarning(feedback.message);
        return;
      }
      if (data.code === 'step_locked') {
        notifyError(feedback.message);
        return;
      }
      if (feedback.resetStep) resetLabelStep();
    }
    setError(msg);
    notifyError(msg);
    void refreshDetailQuiet();
  };

  const runLabelAction = async (request) => {
    if (labelBusyRef.current) return null;
    labelBusyRef.current = true;
    setLabelBusy(true);
    setError('');
    try {
      const { data } = await request();
      return data;
    } catch (requestError) {
      handleLabelError(requestError);
      return null;
    } finally {
      labelBusyRef.current = false;
      setLabelBusy(false);
    }
  };

  const applyLabelResult = (data, { scannedCode = '' } = {}) => {
    if (!data) return;
    if (data.job?.id && Array.isArray(data.job.items)) setDetail(data.job);
    const next = nextLabelingState(data, labelStateRef.current, { mode: LABELING_MODES.DURING_PICKING });
    let nextCurrent = data.kind === 'product' && next.current?.is_product && scannedCode
      ? { ...next.current, scanned_barcode: scannedCode }
      : next.current;
    let nextStep = next.step;
    // Проверенный заказ мог быть любым из напечатанных — следующий непроверенный открываем сразу.
    if (data.kind === 'label' && nextStep === LABELING_STEPS.IDLE) {
      const jobItems = data.job?.id && Array.isArray(data.job.items) ? data.job.items : (detail?.items || []);
      const following = resumePickingVerify(
        jobItems.map((item) => normalizePickingJobItem(item, marketplace)),
        marketplace,
        { excludeKeys: [data.order?.order_key] },
      );
      if (following) {
        nextCurrent = following;
        nextStep = LABELING_STEPS.VERIFY;
      }
    }
    setLabelCurrent(nextCurrent);
    setLabelStep(nextStep);
    if (next.step === LABELING_STEPS.IDLE || data.kind === 'order' || data.kind === 'product') {
      setPrintError(null);
    } else if (data.kind === 'confirm' || data.kind === 'label') {
      setPrintError(data.print_error || null);
    }
    const passive = data.kind === 'unrecognized' || data.kind === 'notice';
    if (data.kind === 'unrecognized') {
      notifyWarning(SCAN_UNRECOGNIZED_MESSAGE);
    } else if (data.kind === 'notice') {
      if (data.notice) notifyInfo(data.notice);
    } else if (data.kind === 'label' && next.step === LABELING_STEPS.IDLE) {
      const ext = data.order?.external_id || data.order?.order_key || '';
      if (data.notice) notifyInfo(data.notice);
      else notifySuccess(`Проклеено${ext ? ` · заказ ${ext}` : ''}`);
    } else if (data.notice) {
      setNotice(data.notice);
    }
    if (data.print_error) notifyError(data.print_error.message || 'Не удалось отправить на печать');
    if (!data.job && !passive) void refreshDetailQuiet();
  };

  const requirePrinter = () => {
    if (printerUid) return true;
    setError(NO_PRINTER_MESSAGE);
    notifyError(NO_PRINTER_MESSAGE);
    return false;
  };

  const scanDuringPicking = async (raw) => {
    const code = String(raw || '').trim();
    if (!detail?.id || !code) return;
    if (!takeScan(labelScanRef, code, { busy: labelBusyRef.current })) return;
    const state = labelStateRef.current;
    if (isServiceConfirmCode(code) && serviceScanNeedsPrinter(state.step) && !requirePrinter()) return;
    setNotice('');
    const data = await runLabelAction(() => axios.post(
      pickingLabelingPath(detail.id, 'scan'),
      labelBody({
        barcode: code,
        printer_uid: printerUid || undefined,
        ...scanStepBody(state),
      }),
      { silent: true, timeout: TSD_PRINT_TIMEOUT_MS },
    ));
    if (!data) return;
    printKeys.clear();
    applyLabelResult(data, { scannedCode: code });
  };

  const confirmLabel = async () => {
    const current = labelStateRef.current.current;
    const key = pickingConfirmKey(current);
    if (!detail?.id || !key) return;
    if (!requirePrinter()) return;
    setNotice('');
    const scanned = current.is_product ? (current.scanned_barcode || current.barcode) : null;
    const data = await runLabelAction(() => printKeys.run('picking-label-confirm', `${key}|${printerUid}`, (idempotencyKey) => axios.post(
      pickingLabelingOrderPath(detail.id, key, 'confirm'),
      labelBody({ printer_uid: printerUid, scanned_barcode: scanned || undefined }),
      { silent: true, timeout: TSD_PRINT_TIMEOUT_MS, headers: { 'Idempotency-Key': idempotencyKey } },
    )));
    if (data) applyLabelResult({ kind: 'confirm', ...data });
  };

  const reprintLabel = async () => {
    const current = labelStateRef.current.current;
    if (!detail?.id || !current?.order_key || current.is_product) return;
    if (!requirePrinter()) return;
    const data = await runLabelAction(() => printKeys.run('picking-label-reprint', `${current.order_key}|${printerUid}`, (idempotencyKey) => axios.post(
      pickingLabelingOrderPath(detail.id, current.order_key, 'reprint'),
      labelBody({ printer_uid: printerUid }),
      { silent: true, timeout: TSD_PRINT_TIMEOUT_MS, headers: { 'Idempotency-Key': idempotencyKey } },
    )));
    if (!data) return;
    const order = normalizeLabelingOrder(data.order, marketplace);
    if (order) setLabelCurrent(order);
    if (data.print_error) {
      setPrintError(data.print_error);
      notifyError(data.print_error.message || 'Не удалось отправить на печать');
    } else {
      setPrintError(null);
      setNotice('Этикетка отправлена на печать');
    }
  };

  const onScan = (raw) => (duringPicking ? scanDuringPicking(raw) : resolve(raw));

  const closeJob = async ({ retry = false } = {}) => {
    if (!detail) return;
    setClosing(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/close`,
        withAccountBody(marketplace, accountId || detail.account_id, {}),
      );
      setNotice(retry
        ? 'Закрытие повторено'
        : `Задание ${data?.code || detail.code || ''} закрыто`);
      goToListSoon(`Задание ${data?.code || detail.code || ''} закрыто. Возврат к списку…`);
    } catch (requestError) {
      const data = requestError?.response?.data || {};
      if (data.code === 'labels_unverified') {
        notifyError(labelingErrorMessage(data));
      } else {
        setError(errorText(requestError));
      }
      await loadDetail().catch(() => {});
    } finally {
      setClosing(false);
    }
  };

  const transferPicked = async () => {
    if (!detail) return;
    setTransferring(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/transfer`,
        withAccountBody(marketplace, accountId || detail.account_id, {}),
      );
      const transfer = data?.transfer || null;
      const count = Number(transfer?.items_count) || 0;
      if (transfer?.ms_move_status === 'error') {
        setError(`Партия №${transfer.seq_no} передана, но перемещение МС не выполнено: ${transfer.ms_move_error || 'ошибка'}`);
      } else {
        setNotice(`Партия${transfer?.seq_no != null ? ` №${transfer.seq_no}` : ''} передана ${transferTarget}${count ? ` · ${count} шт` : ''}`);
      }
      const skipped = Number(data?.skipped_unverified ?? transfer?.skipped_unverified) || 0;
      if (skipped > 0) notifyWarning(`Передано ${count}, не проверено и осталось ${skipped}`);
      if (data?.id && Array.isArray(data?.items)) setDetail(data);
      else await loadDetail();
    } catch (requestError) {
      setError(errorText(requestError));
      await loadDetail().catch(() => {});
    } finally {
      setTransferring(false);
    }
  };

  const retryTransferMove = async (transfer) => {
    if (!detail || !transfer?.id) return;
    setRetryingId(transfer.id);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${detail.id}/transfers/${transfer.id}/retry-move`,
        withAccountBody(marketplace, accountId || detail.account_id, {}),
      );
      setNotice(`Перемещение партии №${transfer.seq_no} проведено`);
      if (data?.status === 'completed') {
        goToListSoon(`Задание ${data?.code || detail.code || ''} закрыто. Возврат к списку…`);
      } else if (data?.id && Array.isArray(data?.items)) {
        setDetail(data);
      } else {
        await loadDetail();
      }
    } catch (requestError) {
      setError(errorText(requestError));
      await loadDetail().catch(() => {});
    } finally {
      setRetryingId(null);
    }
  };

  const progress = pickingProgress(detail || {});
  const active = detail && isActivePickingStatus(detail.status);
  const remainingCount = Math.max(0, progress.total - progress.picked);
  const allPicked = Boolean(active && progress.total > 0 && remainingCount === 0);
  const transfers = Array.isArray(detail?.transfers) ? detail.transfers : [];
  const transferErrors = transfers.filter((transfer) => transfer?.ms_move_status === 'error');
  const transferInProgress = transfers.some((transfer) => transfer?.ms_move_status === 'in_progress');
  // Ошибка МС теперь живёт на партии; агрегат задания — только для старых ответов без transfers.
  const moveRetry = !transfers.length && Boolean(detail?.ms_move_required && detail?.ms_move_status === 'error');
  // Режим during_picking: передаются только позиции с проверенной этикеткой.
  const labelCounts = duringPicking ? pickingLabelingCounts(detail, items) : null;
  const unverifiedCount = labelCounts?.unverified || 0;
  const transferableCount = labelCounts ? labelCounts.ready : (detail ? pickingTransferableCount(detail, items) : 0);
  const transferredCount = items.filter((item) => item.transfer_id != null).length
    || Number(detail?.items_transferred) || 0;
  const canTransfer = Boolean(active && transferableCount > 0 && !transferInProgress && !transferErrors.length);
  const closeBlockedByLabels = unverifiedCount > 0;
  const pickBlocked = Boolean(batch) && !canPickTarget(batch);
  const counter = batchCounter(batch);
  const labeledCount = labelCounts?.ready || 0;
  const closePrompt = `Закрыть задание${detail?.code ? ` ${detail.code}` : ''}: передать остаток (${transferableCount}) ${transferTarget}, `
    + `вернуть ${remainingCount} несобранных в «Ожидают сборки».`
    + (detail?.ms_move_required && transferableCount > 0 ? ' Будет создано перемещение МойСклад.' : '');
  const transferPrompt = `Передать ${transferableCount} собранных позиций? Откатить их будет нельзя.`
    + (unverifiedCount > 0 ? `\nНепроверенные (${unverifiedCount}) останутся в задании.` : '')
    + (detail?.ms_move_required ? '\nБудет создано перемещение МойСклад по этой партии.' : '');
  const modeInfo = labelingModeInfo(detail?.labeling_mode);
  const labelDialogOpen = Boolean(duringPicking && labelCurrent && labelStep !== LABELING_STEPS.IDLE);
  let scanHelper = active ? 'Сканируйте товар' : 'Это задание закрыто.';
  if (active && duringPicking) {
    scanHelper = labelBusy
      ? 'Обработка…'
      : labelingStepHint(labelStep, labelCurrent, { mode: LABELING_MODES.DURING_PICKING, context: 'picking' });
  }

  return (
    <>
      <TsdPageBar
        title={detail?.code || 'Подбор'}
        startAdornment={(
          <Tooltip title="К списку">
            <IconButton
              component={RouterLink}
              to={listPath}
              aria-label="К списку"
            >
              <BackIcon />
            </IconButton>
          </Tooltip>
        )}
        extra={duringPicking && active ? (
          <ServiceLabelActions printerUid={printerUid} disabled={labelBusy} />
        ) : undefined}
      />
      <Stack
        spacing={2}
        sx={{
          maxWidth: 900,
          mx: 'auto',
          pb: 4,
          width: '100%',
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        {blockedReason && <Alert severity="warning">{blockedReason}</Alert>}
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" icon={<CheckIcon />} onClose={() => setNotice('')}>{notice}</Alert>}
        {duringPicking && active && !printer.loading && !printerUid && (
          <Alert severity="warning">Принтер не выбран — выберите его в Настройки → Печать</Alert>
        )}

        {loading && <LinearProgress />}

        {!loading && !detail && !blockedReason && (
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">Не удалось открыть задание</Typography>
            <Button component={RouterLink} to={listPath} sx={{ mt: 1 }} startIcon={<BackIcon />}>
              Назад к списку
            </Button>
          </Paper>
        )}

        {detail && (
          <>
            <Paper variant="outlined" sx={{ p: 2, overflow: 'hidden', minWidth: 0 }}>
              <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                  sx={{ minWidth: 0 }}
                >
                  <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                    {detail.code && detail.name && (
                      <Typography
                        variant="bodyMedium"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {detail.name}
                      </Typography>
                    )}
                  </Box>
                  {duringPicking && <Chip size="small" variant="outlined" color={modeInfo.color} label={modeInfo.label} />}
                  <FulfillmentStatusChip kind="pickingJob" status={detail.status} />
                </Stack>
                <LinearProgress variant="determinate" value={progress.percent} sx={{ height: 10, borderRadius: 5 }} />
                <Typography variant="bodyLarge">
                  {progress.picked} из {progress.total} подобрано
                  {labeledCount > 0 ? ` · проклеено ${labeledCount}` : ''}
                  {transferredCount > 0 ? ` · передано ${transferredCount}` : ''}
                </Typography>
                {detail.stock_warnings?.map((warning) => {
                  let action;
                  if (settingsHint(warning.message)) {
                    action = (
                      <Button color="inherit" size="small" component={RouterLink} to="/settings/mp-accounts">
                        К настройкам
                      </Button>
                    );
                  } else if (linkHint(warning.message)) {
                    action = (
                      <Button color="inherit" size="small" component={RouterLink} to="/settings/ms-products">
                        К товарам МС
                      </Button>
                    );
                  }
                  return (
                    <Alert
                      key={`${warning.code}-${warning.message}`}
                      severity={warningSeverity(warning)}
                      action={action}
                      sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {warning.message}
                    </Alert>
                  );
                })}
                {active && unverifiedCount > 0 && (
                  <Alert severity="warning" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {`Не проверено: ${unverifiedCount}. Отсканируйте этикетки перед передачей`}
                    {labelCounts.unverifiedOrders.length > 0
                      ? ` · заказы ${labelCounts.unverifiedOrders.join(', ')}`
                      : ''}
                  </Alert>
                )}
                {allPicked && !closeBlockedByLabels && (
                  <Alert severity="success">Все позиции подобраны. Закройте задание</Alert>
                )}
                {moveRetry && (
                  <Alert severity="error">
                    Ошибка перемещения МС: {detail.ms_move_error}
                  </Alert>
                )}
                {transfers.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="titleSmall">
                      {duringPicking ? 'Партии на склад отгрузки' : 'Партии на проклейку'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                      {transfers.map((transfer) => (
                        <Tooltip key={transfer.id} title={transfer.ms_move_name || transfer.ms_move_error || ''}>
                          <Chip
                            size="small"
                            color={transferMoveInfo(transfer).color}
                            variant={transfer.ms_move_status === 'error' ? 'filled' : 'outlined'}
                            label={transferChipLabel(transfer)}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                    {transferErrors.map((transfer) => (
                      <Alert
                        key={transfer.id}
                        severity="error"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                        action={(
                          <Button
                            color="inherit"
                            size="small"
                            disabled={retryingId != null || transferring || closing}
                            onClick={() => retryTransferMove(transfer)}
                          >
                            {retryingId === transfer.id
                              ? <CircularProgress size={18} color="inherit" />
                              : 'Повторить перемещение'}
                          </Button>
                        )}
                      >
                        Партия №{transfer.seq_no}: перемещение МС не выполнено.
                        {transfer.ms_move_error ? ` ${transfer.ms_move_error}` : ''}
                      </Alert>
                    ))}
                  </Stack>
                )}
                {active && (
                  <Button
                    variant={allPicked ? 'outlined' : 'contained'}
                    color="secondary"
                    onClick={() => setTransferConfirmOpen(true)}
                    disabled={!canTransfer || transferring || closing || resolving || labelBusy}
                    sx={{ minHeight: 52, width: '100%' }}
                  >
                    {transferring
                      ? <CircularProgress size={22} color="inherit" />
                      : `Передать собранное ${transferTarget}${transferableCount ? ` (${transferableCount})` : ''}`}
                  </Button>
                )}
                {active && transferInProgress && (
                  <Typography variant="bodySmall" color="text.secondary">
                    Идёт перемещение МойСклад по предыдущей партии — следующую можно будет передать после него.
                  </Typography>
                )}
                {active && closeBlockedByLabels && (
                  <Typography variant="bodySmall" color="warning.main">
                    {`Сначала проверьте этикетки: ${unverifiedCount} — до этого закрыть задание нельзя`}
                  </Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
                  {active && (
                    <Button
                      variant="contained"
                      color={moveRetry ? 'warning' : (allPicked ? 'success' : 'primary')}
                      onClick={() => setCloseConfirmOpen(true)}
                      disabled={closing || resolving || labelBusy || transferring || transferErrors.length > 0
                        || transferInProgress || closeBlockedByLabels}
                      sx={{
                        minHeight: allPicked ? 64 : 48,
                        width: allPicked ? '100%' : { xs: '100%', sm: 'auto' },
                        fontWeight: allPicked ? 700 : undefined,
                      }}
                    >
                      {closing
                        ? <CircularProgress size={22} color="inherit" />
                        : (moveRetry ? 'Повторить закрытие' : 'Закрыть задание')}
                    </Button>
                  )}
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadDetail}
                    disabled={loading || resolving || closing}
                    sx={{ minHeight: 48, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Обновить
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Не ставим pause при открытом диалоге (пачка и шаг этикетки): сканы идут в него. */}
            <TsdScanCapture
              disabled={!active || resolving || labelBusy}
              onScan={onScan}
              helperText={scanHelper}
              dialogPlaceholder={duringPicking ? 'Штрихкод' : 'Штрихкод товара'}
            />

            <Stack spacing={1} sx={{ minWidth: 0 }}>
              {items.map((item) => {
                const lines = compositionLines(item, marketplace);
                const orderPicked = item.status === 'picked';
                return (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{ p: 1.5, opacity: orderPicked ? 0.65 : 1, overflow: 'hidden', minWidth: 0 }}
                >
                  <Stack spacing={1} sx={{ minWidth: 0 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      gap={1}
                      sx={{ minWidth: 0 }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        {!lines && (
                          <FulfillmentProductThumb
                            product={item.product}
                            task={item.task}
                            marketplace={marketplace}
                            size={48}
                          />
                        )}
                        <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                          <Typography
                            variant="bodyLarge"
                            sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            {lines
                              ? `Заказ ${item.task?.wb_order_id || item.task?.posting_number || ''}`
                              : (item.task?.offer_id || item.product?.article || item.task?.article || `nmID ${item.task?.nm_id}`)}
                          </Typography>
                          {!lines && (
                            <Typography
                              variant="bodySmall"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                            >
                              ШК {item.task?.barcode || item.product?.barcode || '—'} · заказ {item.task?.wb_order_id || item.task?.posting_number}
                            </Typography>
                          )}
                          <B2bChip show={!isLocalWarehouse && isB2bTask(item.task)} sx={{ mt: 0.5 }} />
                          {stockLabel(item.stock) && (
                            <Typography
                              variant="bodySmall"
                              color={stockBlocked(item.stock) ? 'error.main' : 'text.secondary'}
                              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                              {stockLabel(item.stock)}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      {duringPicking ? (
                        <Chip
                          size="small"
                          color={pickingItemStageInfo(item, detail.labeling_mode).color}
                          label={pickingItemStageInfo(item, detail.labeling_mode).label}
                        />
                      ) : item.transfer_id != null ? (
                        <Chip size="small" color="secondary" label="Передано" />
                      ) : (
                        <FulfillmentStatusChip
                          code={orderPicked ? 'picked' : 'pending'}
                          label={orderPicked ? 'Подобран' : 'Ожидает'}
                        />
                      )}
                    </Stack>
                    {lines && lines.map((line) => {
                      const linePicked = line.status === 'picked'
                        || Number(line.qty_picked) >= Number(line.qty_need || line.quantity || 1);
                      const need = Number(line.qty_need || line.quantity || 1);
                      return (
                        <Stack
                          key={line.key || line.offer_id || line.sku || line.article}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ minWidth: 0, pl: { sm: 0.5 } }}
                        >
                          <FulfillmentProductThumb
                            product={line}
                            marketplace={marketplace}
                            size={40}
                          />
                          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <Typography
                              variant="bodyMedium"
                              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                              {lineCode(line)}
                            </Typography>
                            {lineName(line) ? (
                              <Typography
                                variant="bodySmall"
                                color="text.secondary"
                                sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                              >
                                {lineName(line)}
                              </Typography>
                            ) : null}
                            <Typography variant="bodySmall" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              ШК {line.barcode || '—'}
                              {line.sku && String(line.sku) !== String(lineCode(line)) ? ` · SKU ${line.sku}` : ''}
                              {` · ${need} шт`}
                              {Number(line.qty_picked) > 0 && !linePicked
                                ? ` · ${line.qty_picked} из ${need}`
                                : ''}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={linePicked ? 'success' : 'default'}
                            variant={linePicked ? 'filled' : 'outlined'}
                            label={linePicked ? 'подобран' : 'ожидает'}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Paper>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>

      <Dialog
        open={closeConfirmOpen}
        onClose={() => { if (!closing) setCloseConfirmOpen(false); }}
        disableAutoFocus
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Закрыть задание?</DialogTitle>
        <DialogContent>
          <Typography variant="bodyLarge">{closePrompt}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCloseConfirmOpen(false)}
            disabled={closing}
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            color={remainingCount > 0 ? 'warning' : 'primary'}
            onClick={() => {
              setCloseConfirmOpen(false);
              closeJob({ retry: moveRetry });
            }}
            disabled={closing}
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
          >
            {closing ? <CircularProgress size={22} color="inherit" /> : 'Закрыть задание'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={transferConfirmOpen}
        title={duringPicking ? 'Передать на склад отгрузки?' : 'Передать на проклейку?'}
        message={transferPrompt}
        confirmText="Передать"
        confirmColor="primary"
        loading={transferring}
        onClose={() => setTransferConfirmOpen(false)}
        onConfirm={() => {
          setTransferConfirmOpen(false);
          transferPicked();
        }}
      />

      {/* Режим during_picking: одна единица → печать → скан этикетки. На шаге verify закрыть нельзя. */}
      <Dialog
        open={labelDialogOpen}
        onClose={(_event, reason) => {
          if (reason !== 'backdropClick' && labelStep === LABELING_STEPS.CONFIRM && !labelBusy) resetLabelStep();
        }}
        disableAutoFocus
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{labelDialogTitle(labelStep, labelCurrent)}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <LabelingStepCard
              embedded
              current={labelCurrent}
              step={labelStep}
              marketplace={marketplace}
              labelingMode={LABELING_MODES.DURING_PICKING}
              busy={labelBusy}
              readOnly={!active}
              printError={printError}
              onConfirm={confirmLabel}
              onReprint={reprintLabel}
              onCancel={resetLabelStep}
            />
            <Typography variant="bodyMedium" color="text.secondary" textAlign="center">
              {labelBusy ? 'Обработка…' : labelingStepHint(labelStep, labelCurrent, {
                mode: LABELING_MODES.DURING_PICKING,
                context: 'picking',
              })}
            </Typography>
            {labelDialogOpen && error && (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Пачечный скан: диалог не закрывается от сканов, счётчик растёт локально до «Подтвердить». */}
      <Dialog open={Boolean(batch)} onClose={closeConfirmation} disableAutoFocus fullWidth maxWidth="xs">
        <DialogTitle>Подтвердить подбор?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            {batch && <ProductThumb target={batch} marketplace={marketplace} />}
            <Typography
              variant="titleLarge"
              textAlign="center"
              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', px: 1 }}
            >
              {batch?.product?.article
                || (batch?.product?.nm_id ? `nmID ${batch.product.nm_id}` : batch?.product?.name || 'Товар')}
            </Typography>
            {batch?.product?.name && batch?.product?.article && (
              <Typography
                variant="bodyMedium"
                color="text.secondary"
                textAlign="center"
                sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', px: 1 }}
              >
                {batch.product.name}
              </Typography>
            )}
            <Typography variant="bodyLarge" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
              Штрихкод: <strong>{batch?.scanned_barcode || batch?.product?.barcode}</strong>
            </Typography>
            <Typography
              variant="bodyMedium"
              color="text.secondary"
              sx={{ wordBreak: 'break-all', textAlign: 'center' }}
            >
              {marketplace === 'wb' && batch?.product?.nm_id ? `nmID ${batch.product.nm_id} · ` : ''}
              {marketplace === 'ozon' && batch?.product?.sku ? `SKU ${batch.product.sku} · ` : ''}
              {(() => {
                const count = batch?.orders?.length || 0;
                const noun = count === 1 ? 'заказ' : count >= 2 && count <= 4 ? 'заказа' : 'заказов';
                return count === 1 && batch.orders[0]?.wb_order_id != null
                  ? `заказ ${batch.orders[0].wb_order_id}`
                  : count === 1 && batch.orders[0]?.posting_number
                    ? `заказ ${batch.orders[0].posting_number}`
                    : `${count} ${noun}`;
              })()}
            </Typography>
            <B2bChip show={!isLocalWarehouse && Boolean(batch?.product?.is_b2b)} />

            <Paper variant="outlined" sx={{ width: '100%', p: 1.5, borderRadius: 3 }}>
              <Typography variant="headlineSmall" textAlign="center" sx={{ fontWeight: 700 }}>
                {`Товаров ${counter.total}/${counter.current}`}
              </Typography>
              <Typography
                variant="bodyMedium"
                textAlign="center"
                color={counter.remaining > 0 ? 'primary.main' : 'success.main'}
                sx={{ mt: 0.5 }}
              >
                {counter.pending === 0
                  ? `Все ${counter.total} уже собраны`
                  : counter.remaining > 0
                    ? `Отсканируйте остальные — осталось собрать ${counter.remaining}`
                    : 'Все единицы отсканированы'}
              </Typography>
              <Stack direction="row" justifyContent="space-around" sx={{ mt: 1.5 }}>
                {[
                  ['Собрано ранее', counter.picked],
                  ['Отсканировано сейчас', counter.scanned_now],
                  ['Осталось', counter.remaining],
                ].map(([label, value]) => (
                  <Stack key={label} alignItems="center" sx={{ minWidth: 0, px: 0.5 }}>
                    <Typography variant="titleMedium" sx={{ fontWeight: 700 }}>{value}</Typography>
                    <Typography variant="labelSmall" color="text.secondary" textAlign="center">{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>

            {stockLabel(batch?.stock) && (
              <Typography
                variant="bodyMedium"
                color={pickBlocked ? 'error.main' : 'text.secondary'}
                textAlign="center"
              >
                {stockLabel(batch.stock)}
              </Typography>
            )}
            {pickBlocked && (
              <Alert severity="error" sx={{ width: '100%' }}>
                Доступный остаток равен нулю — подтверждение заблокировано.
              </Alert>
            )}
            {Boolean(batch) && error && (
              <Alert severity="error" sx={{ width: '100%' }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeConfirmation} size="large" sx={{ minHeight: 52, flex: 1 }}>Отмена</Button>
          <Button
            variant="contained"
            onClick={confirm}
            disabled={resolving || pickBlocked || counter.scanned_now < 1}
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
          >
            {resolving
              ? <CircularProgress size={22} color="inherit" />
              : `Подтвердить${counter.scanned_now > 0 ? ` (${counter.scanned_now})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
